// Prop business module (Schedule C inputs) for one person. Income comes only from payout events, each counted once;
// documents are evidence that link to those events. Forms are reconciled against the events, never added to them.
import type { TraceStep } from "../calc.ts";
import { D, type Dec, ZERO, fmt } from "../money.ts";
import { RULES_2026 as R } from "../rules/2026.ts";
import { screeningIssues } from "./futures.ts";
import { IssueList, m, resultFrom, usd, type ModuleResult } from "./issues.ts";
import { byId, formName, type InfoReturn, type SourceView } from "./sources.ts";
import type { Decision, EconomicEvent, ExpenseTreatment, Id, Money, Source, YearFile } from "./types.ts";
import { known, yearOf, type Amount, type IsoDate } from "./values.ts";
import { live } from "./yearfile.ts";

type Compensation = Extract<EconomicEvent, { kind: "compensation" }>;
type Expense = Extract<EconomicEvent, { kind: "expense" }>;
type Refund = Extract<EconomicEvent, { kind: "refund" }>;
type Transfer = Extract<EconomicEvent, { kind: "transfer" }>;
type PayerForm = Extract<InfoReturn, { kind: "form_1099nec" | "form_1099misc" | "form_1099k" }>;

export type IncomeBasis = "payout_records" | "processor_receipts" | "info_return_total" | "manual";

export interface PropFirmResult {
  firmId: Id;
  name: string;
  basis: IncomeBasis | null;
  grossIncome: Money;
  payoutIds: Id[];
}

export interface PropResult {
  /** Schedule C line 1: payouts recognized this year, each once. */
  grossIncome: Money;
  /** Recoveries of expenses deducted in an earlier year. */
  otherIncome: Money;
  /** Deducted this year: payout fees plus expenses confirmed as current business expenses, less same-year refunds. */
  currentExpenses: Money;
  /** Everything else, kept separate: startup, capital, personal and not-yet-classified costs. */
  byTreatment: Record<ExpenseTreatment, Money>;
  netProfit: Money;
  perFirm: PropFirmResult[];
  countedExpenseIds: Id[];
}

export interface PropReconciliation {
  firmId: Id;
  personId: Id;
  /** Payouts counted this year: contractual gross, and gross less each payout's fee. */
  eventGross: Money;
  eventNet: Money;
  forms: { sourceId: Id; kind: InfoReturn["kind"]; amount: Amount }[];
  /** 1099-NEC plus 1099-MISC total minus the payouts it's compared with (gross, or net when you chose net reporting). */
  variance: Money | null;
  /** 1099-K total minus gross payouts. A processor's 1099-K describes the same payouts; it's never extra income. */
  kVariance: Money | null;
}

export interface PropAnalysis {
  result: ModuleResult<PropResult>;
  reconciliation: PropReconciliation[];
  withholding: { sourceId: Id; amount: Money }[];
  countedPayouts: Compensation[];
}

const SCREEN_PROP = ["nonresident", "entity", "nonUsd"] as const;
const TREATMENTS: ExpenseTreatment[] = ["current", "startup", "personal", "capital", "unresolved"];

function bestDate(c: Compensation): IsoDate | null {
  return c.dates.paid ?? c.dates.sentByPayer ?? c.dates.approved ?? c.dates.requested ?? c.dates.deposited;
}

function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.abs(Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10)) - Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10))) / 86_400_000;
}

function originOf(e: Compensation, sources: Map<Id, Source>): IncomeBasis | "other" | "missing" {
  if ("manual" in e.createdFrom) return "manual";
  const s = sources.get(e.createdFrom.sourceId);
  if (!s) return "missing";
  return s.kind === "firm_payout_record" ? "payout_records" : s.kind === "processor_receipt" ? "processor_receipts" : "other";
}

const BASIS_LABEL: Record<IncomeBasis, string> = {
  payout_records: "the firm's payout history",
  processor_receipts: "payment processor receipts",
  info_return_total: "the 1099 total",
  manual: "payouts you entered yourself",
};

function money(a: Amount): Dec | null {
  return known(a);
}

export function analyzeProp(yf: YearFile, personId: Id, view: SourceView, decisions: Decision[], issues: IssueList): PropAnalysis {
  const person = yf.people.find((p) => p.id === personId)!;
  const firms = new Map(live(yf.firms).map((f) => [f.id, f]));
  const accounts = new Map(live(yf.accounts).map((a) => [a.id, a]));
  const sources = new Map(view.all.map((s) => [s.id, s]));
  const events = byId(live(yf.events));
  const myAccounts = [...accounts.values()].filter((a) => a.kind === "prop_firm" && a.ownerId === personId).sort((a, b) => (a.id < b.id ? -1 : 1));
  const myComp = events.filter((e): e is Compensation => e.kind === "compensation" && e.personId === personId);
  const myExpenses = events.filter((e): e is Expense => e.kind === "expense" && e.personId === personId);
  const myRefunds = events.filter((e): e is Refund => e.kind === "refund" && e.personId === personId);
  const myTransfers = events.filter((e): e is Transfer => e.kind === "transfer" && e.personId === personId);
  const myForms = view.effectiveForms.filter((f): f is PayerForm => f.kind !== "form_1099b" && f.personId === personId);
  const issue = (x: Omit<Parameters<IssueList["add"]>[0], "module" | "personId">) => issues.add({ ...x, module: "propBusiness", personId });

  const empty: PropAnalysis = { result: { state: "not_applicable", issues: [] }, reconciliation: [], withholding: [], countedPayouts: [] };
  const hasData = myAccounts.length || myComp.length || myExpenses.length || myRefunds.length || myForms.length;
  const listed = yf.household.allPropFirmsListed;
  if (!hasData && listed === "yes") return empty;
  if (listed !== "yes")
    issue({
      code: listed === "no" ? "FIRMS_MISSING" : "FIRMS_UNCONFIRMED",
      severity: "blocker",
      refs: [],
      message: listed === "no" ? "Some prop firms haven't been added yet." : "We need to know every prop firm that paid you this year has been added, including closed accounts.",
      nextAction: listed === "no" ? "Add the remaining firms." : "Confirm every prop firm is listed.",
    });
  if (!hasData) return { ...empty, result: { state: "blocked", partial: null, trace: [], issues: issues.for("propBusiness", personId) } };

  screeningIssues(yf, personId, SCREEN_PROP, "propBusiness", issues);

  for (const a of myAccounts) {
    if (a.firmId === null || !firms.has(a.firmId))
      issue({ code: "ACCOUNT_FIRM_MISSING", severity: "blocker", refs: [a.id], message: `${a.label} isn't tied to a prop firm.`, nextAction: "Choose the firm for this account." });
    if (a.coverageComplete !== "yes")
      issue({
        code: "COVERAGE_INCOMPLETE",
        severity: "blocker",
        refs: [a.id],
        message: a.coverageComplete === "no" ? `Some payouts or fees for ${a.label} are still missing.` : `We need to know every payout and fee for ${a.label} is entered.`,
        nextAction: a.coverageComplete === "no" ? "Add the missing records." : `Confirm everything for ${a.label} is entered, or add what's missing.`,
      });
  }

  // Ownership: a payout belongs to the person who owns the account. A mix-up would put income on the wrong Schedule SE.
  for (const e of events.filter((x): x is Compensation => x.kind === "compensation")) {
    const acct = e.accountId ? accounts.get(e.accountId) : undefined;
    if (acct && acct.ownerId !== e.personId && (acct.ownerId === personId || e.personId === personId))
      issue({
        code: "PERSON_ACCOUNT_MISMATCH",
        severity: "blocker",
        refs: [e.id, acct.id],
        message: `A payout on ${acct.label} is assigned to a different person than the account's owner.`,
        nextAction: "Fix who owns the account or whose payout it is.",
      });
  }

  // Which payouts count: valid, from the chosen basis, recognized this year.
  type Counted = { e: Compensation; firmId: Id; gross: Dec; fee: Dec };
  const counted: Counted[] = [];
  const byFirm = new Map<Id, Compensation[]>();
  for (const e of myComp) {
    const acct = e.accountId ? accounts.get(e.accountId) : undefined;
    if (!acct || acct.kind !== "prop_firm" || !acct.firmId) {
      issue({ code: "PAYOUT_ACCOUNT_MISSING", severity: "blocker", refs: [e.id], message: "A payout isn't tied to a prop firm account.", nextAction: "Choose the account this payout came from." });
      continue;
    }
    byFirm.set(acct.firmId, [...(byFirm.get(acct.firmId) ?? []), e]);
  }
  const firmIds = new Set<Id>([...byFirm.keys(), ...myAccounts.flatMap((a) => (a.firmId ? [a.firmId] : [])), ...myForms.flatMap((f) => (f.firmId ? [f.firmId] : []))]);

  const perFirm: PropFirmResult[] = [];
  const reconciliation: PropReconciliation[] = [];
  const trace: TraceStep[] = [];
  let grossIncome = ZERO;
  let feeExpenses = ZERO;

  for (const firmId of [...firmIds].sort()) {
    const firm = firms.get(firmId);
    const name = firm?.name ?? "Unknown firm";
    const comp = byFirm.get(firmId) ?? [];
    const decided = decisions.find((d): d is Extract<Decision, { kind: "prop_income_basis" }> => d.kind === "prop_income_basis" && d.firmId === firmId && d.personId === personId);
    const feeNet = decisions.some((d) => d.kind === "fee_treatment" && d.firmId === firmId && d.personId === personId && d.treatment === "net_reported_no_fee_deduction");
    const forms = myForms.filter((f) => f.firmId === firmId);
    const necMisc = forms.filter((f) => f.kind === "form_1099nec" || f.kind === "form_1099misc");

    const origins = new Map<Id, ReturnType<typeof originOf>>(comp.map((e) => [e.id, originOf(e, sources)]));
    let basis: IncomeBasis | null = decided?.basis ?? null;
    if (!basis) {
      const kinds = [...new Set(origins.values())].filter((k): k is IncomeBasis => k !== "other" && k !== "missing");
      if (kinds.length > 1)
        issue({
          code: "BASIS_CONFLICT",
          severity: "blocker",
          refs: [firmId, ...comp.map((e) => e.id)],
          message: `Payouts for ${name} were entered from more than one kind of record (${kinds.map((k) => BASIS_LABEL[k]).join(" and ")}). Counting both would count the same money twice.`,
          nextAction: "Choose which kind of record to count. The others will be linked as evidence.",
        });
      else basis = kinds[0] ?? null;
    }

    let firmGross = ZERO;
    let firmNet = ZERO;
    const firmPayoutIds: Id[] = [];

    if (basis === "info_return_total") {
      if (comp.length)
        issue({
          code: "BASIS_CONFLICT",
          severity: "blocker",
          refs: [firmId, ...comp.map((e) => e.id)],
          message: `${name} is set to use the 1099 total, but individual payouts were also entered. They aren't added on top.`,
          nextAction: "Choose one: the 1099 total, or the individual payouts.",
        });
      if (!necMisc.length)
        issue({ code: "INFO_RETURN_MISSING", severity: "blocker", refs: [firmId], message: `${name} is set to use the 1099 total, but no 1099 has been added.`, nextAction: "Add the 1099-NEC or 1099-MISC." });
      for (const f of necMisc) {
        const v = money(f.incomeBox);
        if (!v) issue({ code: "FIELD_MISSING", severity: "blocker", refs: [f.id], message: `The income box on ${formName(f)} isn't entered.`, nextAction: "Enter the amount exactly as printed." });
        else {
          firmGross = firmGross.plus(v);
          firmNet = firmNet.plus(v);
        }
      }
      if (necMisc.length && !feeNet)
        issue({
          code: "FEES_UNKNOWN_UNDER_FORM_TOTAL",
          severity: "info",
          refs: [firmId],
          message: `With the 1099 total for ${name}, payout fees aren't known per payout.`,
          nextAction: "Add any payout fees as expenses so they're deducted.",
        });
    } else {
      for (const e of comp) {
        const origin = origins.get(e.id)!;
        if (origin === "missing")
          issue({ code: "PAYOUT_SOURCE_REMOVED", severity: "review", refs: [e.id], message: "A payout's original record was removed.", nextAction: "Keep the payout if it's real, or remove it too." });
        if (basis && origin !== basis && origin !== "missing" && origin !== "other") {
          issue({
            code: "NOT_BASIS",
            severity: "review",
            refs: [e.id],
            message: `A ${name} payout came from ${BASIS_LABEL[origin]}, but ${name} is counted from ${BASIS_LABEL[basis]}, so it isn't counted.`,
            nextAction: "Link that record to the matching payout instead, or change what's counted.",
          });
          continue;
        }
        const gross = money(e.gross);
        if (!gross) {
          issue({ code: "PAYOUT_AMOUNT_MISSING", severity: "blocker", refs: [e.id], message: `A ${name} payout has no amount.`, nextAction: "Enter the payout amount before fees." });
          continue;
        }
        if (gross.isNeg()) {
          issue({
            code: "NEGATIVE_PAYOUT",
            severity: "blocker",
            refs: [e.id],
            message: `A ${name} payout is negative.`,
            nextAction: "Enter money taken back as a refund or clawback instead of a negative payout.",
          });
          continue;
        }
        let fee = ZERO;
        const f = e.payoutFee;
        if (f.state === "value") {
          fee = new D(f.value);
          if (fee.isNeg() || fee.greaterThan(gross)) {
            issue({ code: "FEE_INVALID", severity: "blocker", refs: [e.id], message: `A ${name} payout fee is negative or bigger than the payout.`, nextAction: "Check the fee." });
            continue;
          }
        } else if (f.state === "unknown" || f.state === "unreadable")
          issue({ code: "FEE_UNKNOWN", severity: "review", refs: [e.id], message: `We don't know whether a ${name} payout had a fee.`, nextAction: "Enter the fee, or mark that there wasn't one." });

        const year = recognitionYear(e, yf.taxYear, decisions, issue, name);
        if (year !== yf.taxYear) continue;
        counted.push({ e, firmId, gross, fee });
        firmPayoutIds.push(e.id);
        firmGross = firmGross.plus(gross);
        firmNet = firmNet.plus(gross.minus(fee));
      }
    }

    // Reconcile the forms against what's counted. Exact; no tolerance.
    const formsTotal = necMisc.reduce<Dec | null>((s, f) => (s && money(f.incomeBox) ? s.plus(money(f.incomeBox)!) : null), ZERO);
    const kForms = forms.filter((f) => f.kind === "form_1099k");
    const kTotal = kForms.reduce<Dec | null>((s, f) => (s && money(f.incomeBox) ? s.plus(money(f.incomeBox)!) : null), ZERO);
    const compared = feeNet ? firmNet : firmGross;
    const variance = necMisc.length && formsTotal ? formsTotal.minus(compared) : null;
    const kVariance = kForms.length && kTotal ? kTotal.minus(firmGross) : null;
    reconciliation.push({
      firmId,
      personId,
      eventGross: m(firmGross),
      eventNet: m(firmNet),
      forms: forms.map((f) => ({ sourceId: f.id, kind: f.kind, amount: f.incomeBox })),
      variance: variance && m(variance),
      kVariance: kVariance && m(kVariance),
    });
    const accepted = (v: Dec) => decisions.some((d) => d.kind === "accept_variance" && d.subjectId === firmId && new D(d.variance).equals(v));
    if (basis !== "info_return_total" && necMisc.length && firmPayoutIds.length === 0)
      issue({
        code: "INFO_RETURN_UNLINKED",
        severity: "blocker",
        refs: [firmId, ...necMisc.map((f) => f.id)],
        message: `${name} sent a 1099 for ${formsTotal ? usd(formsTotal) : "an amount"}, but no payouts from ${name} are counted this year.`,
        nextAction: "Add the payouts, or choose to use the 1099 total for this firm.",
      });
    else if (variance && !variance.isZero() && !accepted(variance)) {
      const matchesOther = formsTotal && (feeNet ? formsTotal.equals(firmGross) : formsTotal.equals(firmNet)) && !firmGross.equals(firmNet);
      issue(
        matchesOther
          ? {
              code: feeNet ? "INFO_RETURN_MATCHES_GROSS" : "INFO_RETURN_MATCHES_NET",
              severity: "review",
              refs: [firmId, ...necMisc.map((f) => f.id)],
              message: feeNet
                ? `${name}'s 1099 (${usd(formsTotal!)}) matches your payouts before fees, but you chose net reporting.`
                : `${name}'s 1099 (${usd(formsTotal!)}) matches your payouts after fees, not before (${usd(firmGross)}). The fees shouldn't be deducted twice.`,
              nextAction: "Choose how this firm reports payouts: before fees (deduct the fees) or after fees (no fee deduction).",
            }
          : {
              code: "INFO_RETURN_VARIANCE",
              severity: "review",
              refs: [firmId, ...necMisc.map((f) => f.id)],
              message: `${name}'s 1099 says ${usd(formsTotal!)}, but the payouts counted add up to ${usd(compared)}: a ${usd(variance)} difference. Neither number is changed.`,
              nextAction: "Look for a missing or extra payout, or ask the firm. If the difference is explained, note why and continue.",
            },
      );
    }
    if (kVariance && !kVariance.isZero() && !accepted(kVariance))
      issue({
        code: "FORM_1099K_VARIANCE",
        severity: "review",
        refs: [firmId, ...kForms.map((f) => f.id)],
        message: `The 1099-K for ${name} payouts says ${usd(kTotal!)}, but the payouts counted add up to ${usd(firmGross)}.`,
        nextAction: "Check which payouts the processor included. A 1099-K is never added on top of the payouts.",
      });
    if (!necMisc.length && basis !== "info_return_total") {
      const firmAccounts = myAccounts.filter((a) => a.firmId === firmId);
      const statuses = firmAccounts.map((a) => a.forms.form_1099nec ?? a.forms.form_1099misc ?? null);
      if (statuses.includes("awaiting"))
        issue({ code: "AWAITING_1099", severity: "blocker", refs: [firmId], message: `Waiting for ${name}'s 1099.`, nextAction: "Add it when it arrives, or say it won't be issued." });
      else if (firmGross.greaterThanOrEqualTo(R.informationReturns.necThreshold) && !statuses.includes("not_issued"))
        issue({
          code: "FORM_EXPECTED",
          severity: "review",
          refs: [firmId],
          message: `Payers usually send a 1099 when they pay you $2,000 or more in a year. None has been added for ${name}.`,
          nextAction: "Add the 1099, mark it as awaited, or say it won't be issued. The income is taxable either way.",
        });
    }

    if (comp.length || necMisc.length) {
      const income = feeNet ? firmNet : firmGross;
      grossIncome = grossIncome.plus(income);
      if (!feeNet && basis !== "info_return_total") feeExpenses = feeExpenses.plus(firmGross.minus(firmNet));
      perFirm.push({ firmId, name, basis, grossIncome: m(income), payoutIds: firmPayoutIds });
      trace.push({ label: `${name}: ${firmPayoutIds.length || (basis === "info_return_total" ? "1099 total" : 0)}${firmPayoutIds.length ? ` payout${firmPayoutIds.length === 1 ? "" : "s"}` : ""}${feeNet ? " after fees" : ""}`, value: fmt(income) });
    }
  }

  duplicatePayouts(counted.map((c) => c.e), decisions, issue);

  // Expenses
  const countedIds = new Set(counted.map((c) => c.e.id));
  const payoutHasFee = (id: Id | null) => {
    const c = counted.find((x) => x.e.id === id);
    return Boolean(c && c.e.payoutFee.state === "value" && !c.fee.isZero());
  };
  const buckets: Record<ExpenseTreatment, Dec> = { current: ZERO, startup: ZERO, personal: ZERO, capital: ZERO, unresolved: ZERO };
  const countedExpenseIds: Id[] = [];
  const expenseAmount = new Map<Id, { bucket: ExpenseTreatment; amount: Dec }>();
  for (const x of myExpenses) {
    const amount = money(x.amount);
    if (!amount || amount.isNeg()) {
      issue({ code: "EXPENSE_AMOUNT_INVALID", severity: "blocker", refs: [x.id], message: `An expense from ${x.vendor || "an unnamed vendor"} has no valid amount.`, nextAction: "Enter the amount paid." });
      continue;
    }
    if (!x.date) {
      issue({ code: "EXPENSE_DATE_MISSING", severity: "blocker", refs: [x.id], message: `An expense from ${x.vendor || "an unnamed vendor"} has no date.`, nextAction: "Enter the date it was charged." });
      continue;
    }
    if (yearOf(x.date) !== yf.taxYear) continue;
    if (x.category === "payout_fee" && x.relatesToEventId && countedIds.has(x.relatesToEventId) && payoutHasFee(x.relatesToEventId)) {
      issue({
        code: "DUPLICATE_FEE",
        severity: "review",
        refs: [x.id, x.relatesToEventId],
        message: "This payout fee is already recorded on the payout itself, so it isn't deducted again.",
        nextAction: "Remove the separate fee, or clear the fee on the payout.",
      });
      continue;
    }
    if (x.relatesToEventId && !countedIds.has(x.relatesToEventId) && !myComp.some((c) => c.id === x.relatesToEventId))
      issue({ code: "EXPENSE_LINK_BROKEN", severity: "review", refs: [x.id], message: "An expense points to a payout that no longer exists.", nextAction: "Link it to the right payout or clear the link." });
    const t = x.treatment ?? "unresolved";
    if (t === "unresolved")
      issue({
        code: "TREATMENT_NEEDED",
        severity: "review",
        refs: [x.id],
        message: `${x.vendor || "An expense"} (${usd(amount)}) isn't classified yet, so it isn't deducted.`,
        nextAction: "Choose whether it's a current business expense, a startup cost, personal, or ask your preparer.",
      });
    if (t === "startup")
      issue({
        code: "STARTUP_COST",
        severity: "review",
        refs: [x.id],
        message: `${x.vendor || "An expense"} is marked as a startup cost. Startup costs follow special rules, so it's listed separately and not deducted here.`,
        nextAction: "Check the treatment with your preparer or filing software's startup-cost section.",
      });
    buckets[t] = buckets[t].plus(amount);
    expenseAmount.set(x.id, { bucket: t, amount });
    countedExpenseIds.push(x.id);
  }

  // Refunds
  let otherIncome = ZERO;
  for (const r of myRefunds) {
    const amount = money(r.amount);
    if (!amount || amount.isNeg() || !r.date) {
      issue({ code: "REFUND_INVALID", severity: "blocker", refs: [r.id], message: "A refund is missing its amount or date.", nextAction: "Enter the refunded amount and date." });
      continue;
    }
    if (yearOf(r.date) !== yf.taxYear) continue;
    if ("eventId" in r.refunds) {
      const target = events.find((e) => e.id === (r.refunds as { eventId: Id }).eventId);
      if (!target || target.personId !== personId) {
        issue({ code: "REFUND_TARGET_MISSING", severity: "blocker", refs: [r.id], message: "A refund points to an expense that isn't here.", nextAction: "Link the refund to the charge it refunds." });
        continue;
      }
      if (target.kind === "compensation") {
        issue({
          code: "CLAWBACK_UNSUPPORTED",
          severity: "blocker",
          refs: [r.id, target.id],
          message: "Money paid back to a firm after a payout needs special handling that this report doesn't cover yet.",
          nextAction: "Ask your preparer how to report the repayment.",
        });
        continue;
      }
      const spent = expenseAmount.get(target.id);
      if (!spent) {
        issue({
          code: "REFUND_OF_UNCOUNTED",
          severity: "review",
          refs: [r.id, target.id],
          message: "This refund is for a charge that isn't counted this year.",
          nextAction: "If the charge was deducted in an earlier year, mark the refund as a prior-year refund.",
        });
        continue;
      }
      const applied = amount.greaterThan(spent.amount) ? spent.amount : amount;
      if (amount.greaterThan(spent.amount))
        issue({ code: "REFUND_EXCEEDS_CHARGE", severity: "review", refs: [r.id, target.id], message: "A refund is bigger than the charge it refunds.", nextAction: "Check both amounts." });
      buckets[spent.bucket] = buckets[spent.bucket].minus(applied);
      spent.amount = spent.amount.minus(applied);
    } else if (r.refunds.deductedThen === null) {
      issue({
        code: "PRIOR_REFUND_UNKNOWN",
        severity: "blocker",
        refs: [r.id],
        message: `A ${usd(amount)} refund is for a charge from an earlier year.`,
        nextAction: "Say whether that charge was deducted on that year's return. If it was, the refund counts as income now.",
      });
    } else if (r.refunds.deductedThen) otherIncome = otherIncome.plus(amount);
  }

  // Transfers out of a prop account must say which payout they deliver; otherwise income might be missing.
  for (const t of myTransfers) {
    const from = t.fromAccountId ? accounts.get(t.fromAccountId) : undefined;
    if (from?.kind !== "prop_firm") continue;
    if (!t.settlesEventId)
      issue({
        code: "TRANSFER_UNLINKED",
        severity: "review",
        refs: [t.id],
        message: `Money moved out of ${from.label}, but it isn't linked to a payout. If it was a payout, the income may be missing.`,
        nextAction: "Link the transfer to its payout, or add the payout.",
      });
    else if (!myComp.some((c) => c.id === t.settlesEventId))
      issue({ code: "TRANSFER_TARGET_MISSING", severity: "blocker", refs: [t.id], message: "A transfer points to a payout that isn't here.", nextAction: "Link it to the right payout." });
  }

  // Every payout-type document should be explained by a payout, an expense or a link.
  const used = new Set<Id>();
  for (const e of events) if (!("manual" in e.createdFrom)) used.add(e.createdFrom.sourceId);
  for (const l of live(yf.links)) used.add(l.sourceId);
  const mine = (s: Source) => s.personId === personId || (s.accountId !== null && myAccounts.some((a) => a.id === s.accountId));
  for (const s of view.all) {
    if (!["firm_payout_record", "processor_receipt", "bank_deposit", "expense_receipt"].includes(s.kind) || !mine(s) || used.has(s.id)) continue;
    issue({
      code: "SOURCE_UNMATCHED",
      severity: "review",
      refs: [s.id],
      message: "A record isn't matched to a payout or expense yet, so it isn't counted.",
      nextAction: "Match it to the payout or expense it shows, or add one.",
    });
  }

  const currentExpenses = feeExpenses.plus(buckets.current);
  const netProfit = grossIncome.plus(otherIncome).minus(currentExpenses);
  trace.push({ label: "Gross receipts", value: fmt(grossIncome) });
  if (!otherIncome.isZero()) trace.push({ label: "Refunds of expenses deducted in earlier years", value: fmt(otherIncome) });
  if (!feeExpenses.isZero()) trace.push({ label: "Payout fees", value: fmt(feeExpenses) });
  trace.push({ label: "Business expenses you confirmed", value: fmt(buckets.current) });
  for (const t of TREATMENTS.filter((t) => t !== "current" && !buckets[t].isZero()))
    trace.push({ label: `Not deducted here: ${t === "unresolved" ? "not yet classified" : t}`, value: fmt(buckets[t]) });
  trace.push({ label: "Net profit (Schedule C line 31)", value: fmt(netProfit) });

  const withholding = myForms.flatMap((f) => (known(f.box4) && !known(f.box4)!.isZero() ? [{ sourceId: f.id, amount: m(known(f.box4)!) }] : []));
  const byTreatment = Object.fromEntries(TREATMENTS.map((t) => [t, m(buckets[t])])) as Record<ExpenseTreatment, Money>;
  const value: PropResult = { grossIncome: m(grossIncome), otherIncome: m(otherIncome), currentExpenses: m(currentExpenses), byTreatment, netProfit: m(netProfit), perFirm, countedExpenseIds };
  return { result: resultFrom(value, trace, issues.for("propBusiness", personId)), reconciliation, withholding, countedPayouts: counted.map((c) => c.e) };
}

/** Constructive receipt: a payout counts in the year it was available to the trader. Year-end payouts get a second look. */
function recognitionYear(e: Compensation, taxYear: number, decisions: Decision[], issue: (x: Omit<Parameters<IssueList["add"]>[0], "module" | "personId">) => void, name: string): number | null {
  const decided = decisions.find((d): d is Extract<Decision, { kind: "recognition_year" }> => d.kind === "recognition_year" && d.eventId === e.id);
  if (decided) return decided.recognizeIn;
  const dates = Object.values(e.dates).filter((d): d is IsoDate => d !== null);
  const years = new Set(dates.map(yearOf));
  if (!e.dates.paid) {
    if (dates.length === 0 || years.has(taxYear))
      issue({
        code: "NEEDS_PAID_DATE",
        severity: "blocker",
        refs: [e.id],
        message: `A ${name} payout has no date for when the money was available to you, so we can't tell which year it belongs to.`,
        nextAction: "Enter the date the money reached your wallet, processor account or bank, whichever came first and could be withdrawn.",
      });
    return null;
  }
  const paidYear = yearOf(e.dates.paid);
  if (years.size > 1 && years.has(taxYear))
    issue({
      code: "YEAR_END_STRADDLE",
      severity: "review",
      refs: [e.id],
      message: `A ${name} payout's dates fall in two different years. It's counted in ${paidYear}, when the money was available to you.`,
      nextAction: "Confirm the money could be withdrawn on that date, or choose the year it belongs to.",
    });
  return paidYear;
}

/** Same person, same firm, matching amounts within a week: possibly one payout entered twice. Never merged automatically. */
function duplicatePayouts(list: Compensation[], decisions: Decision[], issue: (x: Omit<Parameters<IssueList["add"]>[0], "module" | "personId">) => void) {
  const dismissed = (a: Id, b: Id) => decisions.some((d) => d.kind === "dismiss_duplicate" && ((d.a === a && d.b === b) || (d.a === b && d.b === a)));
  for (let i = 0; i < list.length; i++)
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i]!, b = list[j]!;
      if (a.accountId !== b.accountId && a.accountId && b.accountId) continue;
      const da = bestDate(a), db = bestDate(b);
      if (!da || !db || daysBetween(da, db) > 7) continue;
      const ga = known(a.gross), gb = known(b.gross);
      if (!ga || !gb) continue;
      const na = ga.minus(known(a.payoutFee) ?? ZERO), nb = gb.minus(known(b.payoutFee) ?? ZERO);
      if (!(ga.equals(gb) || na.equals(nb) || ga.equals(nb) || na.equals(gb))) continue;
      if (dismissed(a.id, b.id)) continue;
      issue({
        code: "POSSIBLE_DUPLICATE_PAYOUT",
        severity: "review",
        refs: [a.id, b.id].sort(),
        message: `Two payouts of ${usd(ga)} and ${usd(gb)} within a week of each other might be the same payout entered twice.`,
        nextAction: "If they're the same payout, remove one and link its record to the other. If they're separate, say so.",
      });
    }
}

